/**
 * Builds the markdown setup prompt an AI agent reads to install the
 * Vocal Cord MCP server. Shared between `/setup` (rendered) and
 * `/llms.txt` static fallback generation (server-side, see public/).
 */
export function buildAgentPrompt(apiBase: string, apiKey: string): string {
  const mcpUrl = `${apiBase.replace(/\/$/, '')}/mcp`;
  return `# Set up Vocal Cord

These are instructions for AI agents on how to set up VocalCord.

## 1. Install the Vocal Cord MCP server

Use whichever install method your runtime supports
(CLI flag, config file, settings panel, etc.):

- **Name:** \`vocalcord\`
- **Transport:** HTTP
- **URL:** \`${mcpUrl}\`
- **Header:** \`Authorization: Bearer ${apiKey}\`

The token above is yours — it was randomly generated and is ready to use.

If your host won't let you set \`Authorization\`, send the key in
\`x-api-key\`, \`x-auth-token\`, or any of the other names listed at
[/llms.txt](https://vocalcord.io/llms.txt). The \`Bearer \` prefix is
optional everywhere — \`x-api-key: ${apiKey}\` is enough.

### Adding it in claude.ai (web, desktop, mobile)

There's no config file there, so the key goes in a form field:

1. **Settings → Connectors → Add custom connector**
2. **MCP server URL:** \`${mcpUrl}\`
3. **Authentication: No sign-in** — Vocal Cord has no OAuth flow, so
   "Sign in now" can't complete. Picking an OAuth mode also greys out the
   \`Authorization\` header, since OAuth reserves it.
4. **Request headers** → name \`x-api-key\`, value \`${apiKey}\`

## 2. Find the MCP tools to confirm

You should see new tools exposed by \`vocalcord\` including \`SendMessage\`.

If you don't see them, you may have to reload the MCP server or ask the user
to restart your session to pick up the new MCP server.

## 3. Send a welcome message

Try sending a message to the user with \`SendMessage\` to confirm you can reach them.

## 4. (OpenClaw users only) Install the plugin

If your runtime is OpenClaw, also install \`@vocalcord/openclaw-plugin\` so
inbound replies wake the agent in real time and pull-style channels stay
synced:

\`\`\`bash
npm install @vocalcord/openclaw-plugin
\`\`\`

Then add to your OpenClaw config:

\`\`\`yaml
plugins:
  vocalcord-openclaw-plugin:
    apiKey: ${apiKey}
    hookToken: <your hooks.token>
\`\`\`

Restart the gateway and the plugin will register the MCP server, set up
real-time inbound (webhook with WebSocket fallback), and schedule a
6-hourly pull-sync for Discord / Matrix. Other delivery options
(webhook, WebSocket, desktop app) are described in
[/llms.txt](https://vocalcord.io/llms.txt).

That's all!`;
}

/**
 * The page's own origin, so the dev stage (`dev.vocalcord.io`) hands
 * out dev URLs instead of silently onboarding users against prod.
 * Falls back to prod for non-browser contexts (tests).
 */
export const SITE_ORIGIN =
  typeof window !== 'undefined' && window.location.origin.startsWith('http')
    ? window.location.origin
    : 'https://vocalcord.io';

export const SETUP_URL = `${SITE_ORIGIN}/setup`;

export const HUMAN_PROMPT = `Set up Vocal Cord from ${SETUP_URL}`;
